import React from 'react';
import {
  Text,
  TextSize,
  TextColor,
  TextHeading,
  TextProps,
  TextWeight,
} from '@components/text/Text';

type AriaLevel = 1 | 2 | 3 | 4 | 5 | 6;

export const HEADING_SIZES = ['small', 'medium', 'large'] as const satisfies readonly TextSize[];
export const HEADING_COLORS = [
  'primary',
  'secondary',
  'tertiary',
  'disabled',
] as const satisfies readonly TextColor[];
export const HEADING_WEIGHTS = [
  'black',
  'bold',
  'semibold',
  'regular',
] as const satisfies readonly TextWeight[];

type SupportedTextSize = (typeof HEADING_SIZES)[number];
type SupportedTextColor = (typeof HEADING_COLORS)[number];
type SupportedTextWeight = (typeof HEADING_WEIGHTS)[number];

export interface HeadingProps
  extends Omit<TextProps, 'size' | 'color' | 'component' | 'variant' | 'weight'> {
  size?: SupportedTextSize;
  variant?: TextHeading;
  color?: SupportedTextColor;
  ariaLevel?: AriaLevel;
  weight?: SupportedTextWeight;
  tabIndex?: number;
}

/**
 * Heading Component
 * @param id - A unique identifier for the element.
 * @param children - Any React.ReactNode component.
 * @param className - Any additional classname to the component.
 * @param size - Defines the size of the text.
 * @param variant - Choose between any of the Typography available variants.
 * @param weight - Defines the weight of the text.
 * @param color - Defines the color of the text.
 * @param align - Defines the alignment of the text.
 * @param lineClamp - Visually truncate the text to the specified number of lines.
 * @param overflowWrap - How truncation is handled when text overflows the line.
 * @param ariaLevel - Allows you to override the default heading level for the given size.
 * @param tabIndex - Defines its position in the navigation order.
 */
export const Heading: React.FC<HeadingProps> = ({
  id,
  children,
  className,
  size = 'large',
  variant = 'heading-01',
  weight = 'bold',
  color = 'primary',
  align = 'start',
  lineClamp,
  overflowWrap,
  ariaLevel = 1,
  tabIndex,
  ...rest
}) => {
  return (
    <Text
      id={id}
      variant={variant}
      component={`h${ariaLevel}`}
      weight={weight}
      align={align}
      color={color}
      size={size}
      overflowWrap={overflowWrap}
      lineClamp={lineClamp}
      className={className}
      tabIndex={tabIndex}
      {...{ ...rest, [`aria-level`]: ariaLevel }}>
      {children}
    </Text>
  );
};
